import AppButton from '@nxsys/components/AppButton';
import {
  defaultHolidayPayCalcOptions,
  fromLeaveYearStart,
  holidayPaymentMethodOptions,
  leaveYearStartDateOptions,
  toLeaveYearStart,
} from '@nxsys/constants/AppConstants';
import { PAYMENT_MODEL_ENUM } from '@nxsys/constants/AppEnums';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { UpdateCompanyByIdParams } from '@nxsys/types/api/company';
import { Icons } from '@nxsys/types/icons';
import { ICompany } from '@nxsys/types/models/company';
import { PAYMENT_MODEL_TEMPLATES } from '@nxsys/utils';
import { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import PaymentModelForm, {
  PaymentModelFormRef,
  PaymentModelFormValues,
} from './components/PaymentModelForm';

type Props = {
  data?: ICompany;
  updateData: (params: UpdateCompanyByIdParams) => Promise<void>;
};

const PaymentModelTab: React.FC<Props> = ({ data, updateData }) => {
  const { messages } = useTypedIntl();
  const { isBureau, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;
  const formRef = useRef<PaymentModelFormRef>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const handleSubmit = useCallback(
    async (values: PaymentModelFormValues) => {
      setIsUpdating(true);
      try {
        const paymentModel = [];
        if (values.includedPaye) paymentModel.push(PAYMENT_MODEL_ENUM.PAYEE);
        if (values.includedOPayehybrid)
          paymentModel.push(PAYMENT_MODEL_ENUM.PAYE_HYBRID);
        if (values.includedCis) paymentModel.push(PAYMENT_MODEL_ENUM.CIS);
        if (values.includedOPayeFth)
          paymentModel.push(PAYMENT_MODEL_ENUM.PAYE_FTH);
        if (values.includedHybrid) paymentModel.push(PAYMENT_MODEL_ENUM.HYBRID);
        if (values.includedUmbrella)
          paymentModel.push(PAYMENT_MODEL_ENUM.UMBRELLA);

        // NX-6284: the single dropdown value ("day-month") decodes back to
        // the two numbers the API still wants; default 1 April if unset.
        const leaveYearStart = fromLeaveYearStart(
          values.holidayLeaveYearStart?.value,
        );

        const params: UpdateCompanyByIdParams = {
          paymentModel,
          holidayPayCalculationRule:
            values.holidayPayCalculationRule?.value ?? null,
          payePayslipTemplate: values.payePayslipTemplate?.value ?? null,
          enableHolidayPay: values.enableHolidayPay ?? false,
          payeHolidayPayRate: values.payeHolidayPayRate
            ? Number(values.payeHolidayPayRate)
            : null,
          holidayPayMethod: values.holidayPayMethod?.value ?? null,
          includeInTimesheetRate: values.includeInTimesheetRate ?? null,
          splitSalaryAndBonus: values.splitSalaryAndBonus ?? null,
          umbrellaPayslipTemplate:
            values.umbrellaPayslipTemplate?.value ?? null,
          enableHolidayPayUmbrella: values.enableHolidayPayUmbrella ?? null,
          umbrellaHolidayPayRate: values.umbrellaHolidayPayRate ?? null,
          umbrellaHolidayPayMethod:
            values.umbrellaHolidayPayMethod?.value ?? null,
          reclaimAppLevyUmbrella: values.reclaimAppLevyUmbrella ?? false,
          accrueEmploymentCosts: values.accrueEmploymentCosts ?? null,
          hybridPayePayslipTemplate:
            values.hybridPayePayslipTemplate?.value ?? null,
          cisPayslipTemplate: values.cisPayslipTemplate?.value ?? null,
          reclaimAppLevyHybrid: values.reclaimAppLevyHybrid ?? false,
          hybridPayslipTemplate: values.hybridPayslipTemplate?.value ?? null,
          // NX-6284: Ireland-only leave year start, default 1 April. Shown
          // (and sent) only for non-bureau Ireland — bureau Ireland sets this
          // in the Payroll Setting tab instead, which must not be clobbered
          // by a default sent from this form's unmounted fields.
          ...(!isBureau && isIreland
            ? {
                holidayLeaveYearStartDay: leaveYearStart?.day ?? 1,
                holidayLeaveYearStartMonth: leaveYearStart?.month ?? 4,
              }
            : {}),
          taxFreePaymentDesc: values.taxFreePaymentDesc ?? null,
          payeETHPayslipTemplate: values.payeETHPayslipTemplate?.value ?? null,
          payeFthMarginAdjustment: values.payeFthMarginAdjustment ?? null,
        };

        await updateData(params);
      } catch (error) {
        console.error(error);
      } finally {
        setIsUpdating(false);
      }
    },
    [messages, updateData, isBureau, isIreland],
  );

  return (
    <div className="PaymentModelTab">
      {data && (
        <>
          <PaymentModelForm
            formRef={formRef}
            onSubmit={handleSubmit}
            isBureau={isBureau}
            isIreland={isIreland}
            defaultValues={{
              includedPaye: data?.paymentModel?.includes(
                PAYMENT_MODEL_ENUM.PAYEE,
              ),
              includedOPayehybrid: data?.paymentModel?.includes(
                PAYMENT_MODEL_ENUM.PAYE_HYBRID,
              ),
              includedCis: data?.paymentModel?.includes(PAYMENT_MODEL_ENUM.CIS),
              includedOPayeFth: data?.paymentModel?.includes(
                PAYMENT_MODEL_ENUM.PAYE_FTH,
              ),
              includedHybrid: data?.paymentModel?.includes(
                PAYMENT_MODEL_ENUM.HYBRID,
              ),
              includedUmbrella: data?.paymentModel?.includes(
                PAYMENT_MODEL_ENUM.UMBRELLA,
              ),
              holidayPayCalculationRule:
                defaultHolidayPayCalcOptions.find(
                  (item) => item.value === data?.holidayPayCalculationRule,
                ) ?? null,
              payePayslipTemplate:
                PAYMENT_MODEL_TEMPLATES?.PAYE?.find(
                  (item) => item.value === data?.payePayslipTemplate,
                ) ?? null,
              enableHolidayPay: data?.enableHolidayPay ?? false,
              payeHolidayPayRate: data?.payeHolidayPayRate?.toString() ?? '',
              holidayPayMethod:
                holidayPaymentMethodOptions.find(
                  (item) => item.value === data?.holidayPayMethod,
                ) ?? null,
              includeInTimesheetRate: data?.includeInTimesheetRate ?? null,
              splitSalaryAndBonus: data?.splitSalaryAndBonus ?? null,
              umbrellaPayslipTemplate:
                PAYMENT_MODEL_TEMPLATES?.UMBRELLA?.find(
                  (item) => item.value === data?.umbrellaPayslipTemplate,
                ) ?? null,
              enableHolidayPayUmbrella: data?.enableHolidayPayUmbrella ?? null,
              umbrellaHolidayPayRate: data?.umbrellaHolidayPayRate ?? null,
              umbrellaHolidayPayMethod:
                holidayPaymentMethodOptions.find(
                  (item) => item.value === data?.umbrellaHolidayPayMethod,
                ) ?? null,
              reclaimAppLevyUmbrella: data?.reclaimAppLevyUmbrella ?? null,
              accrueEmploymentCosts: data?.accrueEmploymentCosts ?? null,
              hybridPayePayslipTemplate:
                PAYMENT_MODEL_TEMPLATES?.PAYE_HYBRID?.find(
                  (item) => item.value === data?.hybridPayePayslipTemplate,
                ) ?? null,
              cisPayslipTemplate:
                PAYMENT_MODEL_TEMPLATES?.CIS?.find(
                  (item) => item.value === data?.cisPayslipTemplate,
                ) ?? null,
              reclaimAppLevyHybrid: data?.reclaimAppLevyHybrid ?? false,
              hybridPayslipTemplate:
                PAYMENT_MODEL_TEMPLATES?.HYBRID?.find(
                  (item) => item.value === data?.hybridPayslipTemplate,
                ) ?? null,
              // NX-6284: default 1 April when unset (new or pre-migration company).
              holidayLeaveYearStart:
                leaveYearStartDateOptions.find(
                  (item) =>
                    item.value ===
                    toLeaveYearStart(
                      (data as any)?.holidayLeaveYearStartDay ?? 1,
                      (data as any)?.holidayLeaveYearStartMonth ?? 4,
                    ),
                ) ?? null,
              taxFreePaymentDesc: data?.taxFreePaymentDesc ?? null,
              payeETHPayslipTemplate:
                PAYMENT_MODEL_TEMPLATES?.PAYE_FTH?.find(
                  (item) => item.value === data?.payeETHPayslipTemplate,
                ) ?? null,
              payeFthMarginAdjustment: data?.payeFthMarginAdjustment ?? null,
            }}
          />
          <div className="flex items-center justify-start gap-3 pt-4">
            <AppButton
              type="primary"
              name={messages['common.save']}
              leftIcon={
                <Icons.IconCheckCircle
                  color={defaultTheme.theme.palette.white}
                />
              }
              onClick={() => formRef.current?.submit()}
              loading={isUpdating}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default PaymentModelTab;
